import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { defer, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

export interface MediaInstance {
	media_id?: number | string;
	resource_type?: string;
	isSpokenFor?: boolean;
	$delete?: () => void;
}

export interface UploadingFile {
	$uploading?: () => boolean;
	$cancel?: () => void;
	[key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class UmcInstancesService {
	public readonly instances: MediaInstance[] = [];
	public file?: UploadingFile;

	public constructor (
		private dialog: MatDialog,
		private translate: TranslateService
	) {}

	public registerMedia (media: MediaInstance, type: string): void {
		if (!this.instances.includes(media)) {
			media.resource_type = type;
			this.instances.push(media);
		}
	}

	public unregisterMedia (media: MediaInstance): void {
		const index = this.instances.indexOf(media);
		if (index >= 0) {
			this.instances.splice(index, 1);
		}
	}

	public setMediaSpokenFor (media: MediaInstance): void {
		const found = this.instances.find((instance) => instance.media_id === media.media_id);
		if (found) {
			found.isSpokenFor = true;
		}
	}

	public isMediaSpokenFor (): MediaInstance | undefined {
		return this.instances.find((instance) => instance.isSpokenFor && instance.resource_type !== 'session');
	}

	public cleanupOrphanedMedia (): void {
		this.instances.forEach((instance) => {
			if (!instance.isSpokenFor && instance.resource_type !== 'comment') {
				instance.$delete?.();
			}
		});
	}

	public registerFile (file: UploadingFile): void {
		this.file = file;
	}

	public canChangeTab$ (): Observable<void> {
		return defer(() => {
			const hasUploadingProp = this.file != null && Object.prototype.hasOwnProperty.call(this.file, '$uploading');
			if (hasUploadingProp) {
				return this.openSwitchTabsDialog('modal-umc_switch-tabs-uploading')
					.pipe(tap(() => this.file?.$cancel?.()));
			}

			const spokenFor = this.isMediaSpokenFor();
			if (spokenFor) {
				return this.openSwitchTabsDialog('modal-umc_switch-tabs-recording')
					.pipe(tap(() => spokenFor.$delete?.()));
			}

			return of(void 0);
		});
	}

	public canChangeTab (): Promise<void> {
		return firstValueFrom(this.canChangeTab$());
	}

	private openSwitchTabsDialog (messageKey: string): Observable<void> {
		const dialogRef = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('common_warning'),
				message: this.translate.instant(messageKey),
				confirmText: this.translate.instant('common_ok'),
				cancelText: this.translate.instant('common_cancel')
			},
			disableClose: true
		});

		return dialogRef.afterClosed().pipe(
			switchMap((confirmed) => confirmed === true ? of(void 0) : throwError(() => new Error('cancelled')))
		);
	}
}
